import { type ISettingsParam, Logger } from 'tslog';

type LogLevel = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace' | 'silly';

export interface LoggerFilterConfig {
  minLevel?: LogLevel;
  allowedCategories?: string[];
  blockedCategories?: string[];
  showUnnamedLoggers?: boolean;
}

const logLevelsMap = {
  /* eslint no-console: 0 */
  fatal: { level: 5, method: console.warn },
  error: { level: 5, method: console.warn },
  warn: { level: 4, method: console.warn },
  info: { level: 3, method: console.info },
  debug: { level: 2, method: console.debug },
  trace: { level: 0, method: console.log },
  silly: { level: 0, method: console.log },
};

export const DEFAULT_LOG_LEVEL: LogLevel = 'warn';

let debugEnabled = false;
export function isDebugEnabled() {
  return debugEnabled;
}

let currentFilters: LoggerFilterConfig = {};
export function getLoggerFilters(): LoggerFilterConfig {
  return { ...currentFilters };
}

export const memoryLogs: string[] = [];

export function getLogger(debug?: boolean, settings?: ISettingsParam<unknown>): Logger<unknown> {
  const debugFlagFromLocalStorage = getLoggerDebugFlagFromLocalStorage();
  debugEnabled = debugFlagFromLocalStorage === true || debug === true;

  // Load filters from storage
  currentFilters = getFiltersFromStorage();

  const logLevel = currentFilters.minLevel ?? (debugEnabled ? 'trace' : DEFAULT_LOG_LEVEL);

  const logger = new Logger({
    minLevel: logLevelsMap[logLevel]?.level ?? 0,
    type: debugEnabled ? 'pretty' : 'json',
    hideLogPositionForProduction: true,
    metaProperty: 'meta',
    prettyLogTimeZone: 'UTC',
    stylePrettyLogs: false, // Disable colors because Firefox doesn't support them
    prettyLogTemplate: '{{hh}}:{{MM}}:{{ss}}:{{ms}}\t{{logLevelName}}\t{{name}}\t',
    overwrite: {
      formatLogObj: (maskedArgs: unknown[]) => ({ args: maskedArgs, errors: [] }),
      transportFormatted: (logMetaMarkup, logArgs) => {
        const logLevel = logMetaMarkup
          .split('\t')[1]
          .replace(/[^A-Z]/g, '')
          .toLowerCase() as LogLevel;

        const loggerName = logMetaMarkup.split('\t')[2]?.trim();

        // Apply filters
        if (!shouldLog(logLevel, loggerName, currentFilters)) {
          return;
        }

        logLevelsMap[logLevel].method(logMetaMarkup, ...logArgs);
      },
    },
    ...settings,
  });

  if (debugEnabled) {
    logger.attachTransport((logObj) => {
      const message = Object.keys(logObj)
        .filter((k) => /^\d+$/.test(k)) // numeric keys are the arguments
        .map((k) => logObj[k as unknown as number])
        .map((arg) => (typeof arg === 'string' ? arg : JSON.stringify(arg)))
        .join(' ');

      const _formatted = `[${logObj.meta.date.toISOString()}] [${logObj.meta.logLevelName}] ${message}`;

      // Keep memory logs size limited
      if (memoryLogs.length > 10000) {
        memoryLogs.shift();
      }

      memoryLogs.push(_formatted);
    });
  }

  return logger;
}

function getLoggerDebugFlagFromLocalStorage(): boolean {
  let valueFromStorage: string | null = null;
  try {
    valueFromStorage = sessionStorage?.getItem('miplayer::debug') ?? localStorage?.getItem('miplayer::debug');
  } catch (_error) {
    // Ignore error
  }

  return valueFromStorage === 'true';
}

function getFiltersFromStorage(): LoggerFilterConfig {
  let valueFromStorage: string | null = null;
  try {
    valueFromStorage = sessionStorage?.getItem('miplayer::logFilters') ?? localStorage?.getItem('miplayer::logFilters');
  } catch (_error) {
    // Ignore error
  }

  if (!valueFromStorage) {
    return {};
  }

  try {
    return JSON.parse(valueFromStorage) as LoggerFilterConfig;
  } catch (_error) {
    console.warn('Failed to parse logger filters from storage', _error);
    return {};
  }
}

function shouldLog(logLevel: LogLevel, loggerName: string | undefined, filters: LoggerFilterConfig): boolean {
  // Normalize empty/undefined logger names to 'default'
  const categoryName = loggerName && loggerName.trim() !== '' ? loggerName : 'default';

  // Check category filters
  if (filters.blockedCategories?.includes(categoryName)) {
    return false;
  }
  if (filters.allowedCategories && filters.allowedCategories.length > 0) {
    if (!filters.allowedCategories.includes(categoryName)) {
      return false;
    }
  }

  // Check if unnamed loggers should be shown (backwards compatibility)
  if (categoryName === 'default' && filters.showUnnamedLoggers === false) {
    return false;
  }

  // Check log level filter
  if (filters.minLevel) {
    const currentLevelValue = logLevelsMap[logLevel]?.level ?? 0;
    const minLevelValue = logLevelsMap[filters.minLevel]?.level ?? 0;
    if (currentLevelValue < minLevelValue) {
      return false;
    }
  }

  return true;
}
